import type {
  ConditionFieldRow,
  StatementCondition,
} from "../../../../api/types"
import { type ConditionSpec } from "./conditions"

/**
 * Persisted `ConditionFieldRow` lookups: the read side of the condition-field
 * contract, shared by BOTH the review-mask's own display (`studio-v2/reviewmask
 * /substanceTable.ts`) and the flow's pure derivation (`flowState.ts`). Lives in
 * `studio/` rather than `studio-v2/mapping/` (its original home) because
 * `flowState.ts` may not depend on `studio-v2/`, and depending on it would
 * invert the dependency direction (studio-v2 is built ON studio, never the
 * reverse); `conditions.ts` stays a thin CMS-content reader per its own header,
 * not a place for row-lookup plumbing. `studio-v2/mapping/conditionRowMerge.ts`
 * re-exports `normalizeCas`/`conditionFieldValue` from here so its eight
 * existing import sites are untouched.
 */

/**
 * A CAS string, folded the way `connect_m3.py`'s `_norm` folds text there:
 * case-fold + collapse whitespace runs to a single space, hyphens/digits
 * untouched, as `' '.join(str(value).lower().split())` in Python, mirrored
 * here as `.toLowerCase().split(/\s+/).filter(Boolean).join(" ")`. `null`/
 * empty/whitespace-only all fold to `null` so "no substance" is one
 * identity, not several.
 *
 * PARITY, PRECISELY: `_norm` is what M3 uses to match a disclosure's raw
 * text against the registry AT EXTRACTION TIME; it does NOT govern the
 * persisted `ConditionFieldRow.substanceCas` address itself. That address is
 * decided by `AddressBook.canonical_bare` / `substance_by_bare`
 * (mc_declaration_condition_record.py), which is a plain, case-SENSITIVE
 * Python dict lookup with no folding at all (`bare_by_substance.get(...,
 * bare)`). Folding case here is still safe against that exact-match dict
 * because its two possible key shapes can never actually collide on case: a
 * real CAS number is digits-and-hyphens only (the CAS RN format has no
 * letters, ever), and the one alphanumeric fallback key,
 * `McUniversalSubstance.id`, is always `str(uuid.uuid4())`, which Python
 * renders lowercase-only. So this fold either lowercases a value the
 * backend already stores lowercase, or touches a value with no letters to
 * begin with; it can never manufacture a mismatch against a real stored
 * key.
 *
 * WHY THIS EXISTS: a persisted `ConditionFieldRow.substanceCas` is the
 * MASTER substance's own canonical bare key (`AddressBook.canonical_bare`),
 * while `StatementCondition.cas` used to key a unit's fields here is the RAW
 * text D6 extracted from the document. The two usually read identically,
 * but a stray space or a different case in the extracted text must not turn
 * an existing concentration/field row invisible (the read side,
 * `conditionFieldValue` below) or fork a second, uneditable row at a
 * near-duplicate address (the write side, `useConditionFieldWrites`,
 * canonicalizes a group's address through this same function before
 * building any `ConditionFieldRow`; see its own doc comment).
 */
export function normalizeCas(value: string | null | undefined): string | null {
  const normalized = (value ?? "")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .join(" ")
  return normalized || null
}

/** A Map keyed level by level: exact per key (`null` and `undefined` stay apart,
 *  as `===` keeps them apart), never a joined string that two keys could share. */
type Nested<T> = Map<unknown, Nested<T> | T>

function nestedGet<T>(
  root: Nested<T>,
  keys: readonly unknown[],
): T | undefined {
  let node: Nested<T> | T | undefined = root
  for (const key of keys) {
    if (node === undefined) return undefined
    node = (node as Nested<T>).get(key)
  }
  return node as T | undefined
}

/** Sets the leaf only when none is there yet: the FIRST row wins, as `find`. */
function nestedSetFirst<T>(
  root: Nested<T>,
  keys: readonly unknown[],
  value: T,
): void {
  let node = root
  for (const key of keys.slice(0, -1)) {
    let next = node.get(key) as Nested<T> | undefined
    if (!next) {
      next = new Map()
      node.set(key, next)
    }
    node = next
  }
  const leaf = keys[keys.length - 1]
  if (!node.has(leaf)) node.set(leaf, value)
}

interface RowIndex {
  length: number
  /** article → normalised CAS → framework → field key → the FIRST such row. */
  byAddress: Nested<ConditionFieldRow>
  /** article → field key → every such row, in row order. */
  byArticleField: Map<string, Map<string, ConditionFieldRow[]>>
}

/**
 * THE ROWS, INDEXED ONCE. A blanket statement over 10k articles carries ~20k
 * rows, and the table reads one value per article per field: a `rows.find` per
 * read was ~2·10⁸ comparisons (plus a `normalizeCas` on every row each time) and
 * froze the review page on sandbox DEC-2026-0007. Built once per row array and
 * kept beside it, keyed by the array's identity (`memberSets.ts`'s rule: the
 * arrays are query-cache data and must never be changed in place; the length
 * check only catches growth, a row replaced at the same length goes unseen).
 * Every reader below answers exactly what its old scan answered.
 */
const ROW_INDEXES = new WeakMap<readonly ConditionFieldRow[], RowIndex>()

function rowIndex(rows: readonly ConditionFieldRow[]): RowIndex {
  const cached = ROW_INDEXES.get(rows)
  if (cached && cached.length === rows.length) return cached
  const index: RowIndex = {
    length: rows.length,
    byAddress: new Map(),
    byArticleField: new Map(),
  }
  for (const r of rows) {
    nestedSetFirst(
      index.byAddress,
      [r.articleNumber, normalizeCas(r.substanceCas), r.framework, r.fieldKey],
      r,
    )
    let byField = index.byArticleField.get(r.articleNumber)
    if (!byField) {
      byField = new Map()
      index.byArticleField.set(r.articleNumber, byField)
    }
    const list = byField.get(r.fieldKey)
    if (list) list.push(r)
    else byField.set(r.fieldKey, [r])
  }
  ROW_INDEXES.set(rows, index)
  return index
}

/** Every row of one article and field key, in row order: the candidates any
 *  per-cell question about that field narrows down from (`findingHealed`). */
export function rowsAt(
  rows: readonly ConditionFieldRow[],
  articleNumber: string,
  fieldKey: string,
): readonly ConditionFieldRow[] {
  return rowIndex(rows).byArticleField.get(articleNumber)?.get(fieldKey) ?? []
}

/** `conditionFieldValue` for one field address across many articles: the
 *  address's CAS normalised once, not once per article (a 10k-article column). */
export function conditionFieldReader(
  rows: readonly ConditionFieldRow[],
  address: {
    substanceCas: string | null
    framework: string | null
    fieldKey: string
  },
): (articleNumber: string) => string {
  const byAddress = rowIndex(rows).byAddress
  const cas = normalizeCas(address.substanceCas)
  return (articleNumber) =>
    nestedGet(byAddress, [
      articleNumber,
      cas,
      address.framework,
      address.fieldKey,
    ])?.value ?? ""
}

/** Read one field's current value out of the row set, by its full identity:
 *  the substance leg compared CAS-normalized (see `normalizeCas`) so a
 *  persisted row addressed by the master CAS still matches the raw
 *  disclosure text this unit reads its address from.
 *  `""` when no row is on record yet, the input's own empty state. */
export function conditionFieldValue(
  rows: readonly ConditionFieldRow[],
  address: {
    articleNumber: string
    substanceCas: string | null
    framework: string | null
    fieldKey: string
  },
): string {
  const row = nestedGet(rowIndex(rows).byAddress, [
    address.articleNumber,
    normalizeCas(address.substanceCas),
    address.framework,
    address.fieldKey,
  ])
  return row?.value ?? ""
}

/** True when a field the spec marks `requiredFor: "verdict"` (rohs exemption
 *  number, china-rohs EFUP) already holds a persisted value for this slot:
 *  the concentration is then a completeness datum, no longer required
 *  (TUR-1544, wave 3). THE ONE PREDICATE both the review-mask's own display
 *  (`substanceTable.ts`'s `concentrationRequired`/`openCounts`) and the
 *  flow's worklist derivation (`flowState.ts::buildStateContext`) read, so a
 *  cell the mask marks "Pflichtfeld offen" cannot silently classify as
 *  complete on the worklist that actually gates the step-3 confirm and the
 *  approve button (L3, live test wave 5).
 */
export function verdictBasisFilled(
  spec: ConditionSpec,
  rows: readonly ConditionFieldRow[],
  framework: string,
  articleNumber: string,
  addressCas: string | null,
): boolean {
  return spec.fields.some(
    (field) =>
      field.requiredFor === "verdict" &&
      conditionFieldValue(rows, {
        articleNumber,
        substanceCas: addressCas,
        framework,
        fieldKey: field.key,
      }) !== "",
  )
}

/**
 * A reviewer's correction to which articles a disclosure applies to.
 *
 * MOVED HERE from `studio-v2/mapping/reviewerBindings.ts` (TUR-1211 W5-L3b,
 * review round 3): `flowState.ts` needs this same fold (it may not depend on
 * `studio-v2/`, this module's own header), and a live verdict-basis check
 * that cannot read a reviewer's own article correction is not a shared rule,
 * it is the bug this file exists to close. Re-exported from
 * `mapping/reviewerBindings.ts` so every existing v2 import site
 * (`SubstanceEditor`, `substanceTable`, `SubstanceRow`,
 * `useConditionFieldWrites`, the legacy editor) keeps working unchanged;
 * `withBinding` (the WRITE side, still v2-only, `flowState.ts` never writes)
 * stays there and imports `isBindingOf`/`BINDING_FIELD_KEY` back from here
 * rather than re-deriving the same predicate.
 *
 * Encoded as the reserved `__connected__` condition-field row, the same
 * fieldKey the older studio's verify stage already writes, so the existing
 * wholesale PUT carries it and no endpoint changes (TUR-1211).
 *
 * A binding is always OPTIONAL. No gate may read it: a declaration with no
 * bindings at all behaves exactly as it did before this module existed.
 *
 * ADDRESSED TO ONE STATEMENT, not to a CAS alone. `statementCardsByFramework`
 * can hold several cards of one framework, and two cards of the same
 * framework can disclose the same CAS: a row keyed by CAS only would let one
 * statement's binding gesture silently override another statement's own
 * extraction binding in `conditionsForArticle`. So a reviewer binding row
 * carries `framework: <card.framework>` and `value: <card.id>` (the
 * statement id) as its address; a row matches only when both agree with the
 * caller's own statement. Every existing reader that folds on (article, cas)
 * alone and ignores `framework`/`value` outright, namely the backend's
 * `_rows_to_connections` (mc_declaration_condition_record.py), the backend's
 * approve narrowing (mc_declaration_review_service.py), and the legacy
 * studio's `rowsToVerifyState` (verifyStatePersistence.ts), keeps reading
 * this row exactly as it read the old one; nothing about this address change
 * is visible to them.
 *
 * A `framework: null` `__connected__` row is NEVER a reviewer binding: that
 * is either the server's own column (written fresh on every re-extraction,
 * for every extracted article x substance pair) or a row the legacy studio
 * wrote. `readBindings` ignores it and `withBinding` leaves it untouched.
 */
export const BINDING_FIELD_KEY = "__connected__"

export function isBindingOf(
  r: ConditionFieldRow,
  framework: string,
  statementId: string,
): boolean {
  return (
    r.fieldKey === BINDING_FIELD_KEY &&
    r.substanceCas != null &&
    r.framework === framework &&
    r.value === statementId
  )
}

/** This statement's own reviewer bindings, keyed by the substance's
 *  normalised CAS. `framework`/`statementId` are the statement's own address
 *  (`card.framework`/`card.id`): a binding addressed to another statement,
 *  to another framework, or a `framework: null` server/legacy row never
 *  matches. */
export function readBindings(
  rows: readonly ConditionFieldRow[],
  framework: string,
  statementId: string,
): Map<string, string[]> {
  const out = new Map<string, string[]>()
  for (const r of rows) {
    if (!isBindingOf(r, framework, statementId)) continue
    // Keyed by the NORMALISED CAS so a lookup from the editor, which normalises
    // its side too, cannot miss on case or whitespace (review, wave 4).
    const cas = normalizeCas(r.substanceCas) as string
    const list = out.get(cas)
    if (list) list.push(r.articleNumber)
    else out.set(cas, [r.articleNumber])
  }
  return out
}

/**
 * Every CAS one statement's condition data addresses, mapped to the articles
 * it applies to: the extraction's own disclosures (`StatementCondition[]`)
 * PLUS any substance a reviewer has connected by hand that no disclosure
 * names at all (a CAS present only in `reviewerBindings`). THE SAME RULE
 * `entryArticles`/`substanceEntries` (`studio-v2/reviewmask/substanceTable.ts`)
 * already apply for the review-mask's own display, re-derived here at the
 * (CAS → articles) grain `buildStateContext`'s live verdict-basis gate needs:
 * `flowState.ts` may not import from `studio-v2/` (this module's own header),
 * so this is the SAME rule, not a second authority for it. Keep the two in
 * lockstep.
 *
 * Precedence per CAS, reviewer wins:
 *   - a reviewer binding for this CAS, when non-empty: exactly those articles
 *     (the reviewer's own correction, `readBindings` above);
 *   - else, when at least one disclosure of this CAS names an explicit
 *     `articleNumbers` binding and NONE is article-agnostic: the union of
 *     those bindings;
 *   - else (fully agnostic, no binding on either side, OR added by the
 *     reviewer with nothing extracted at all): every article in `articles`
 *     (the statement's own resolved scope).
 *
 * A CAS present only via `reviewerBindings` (nothing in `conditions` at all)
 * is exactly the "reviewer connected a substance the extraction never
 * disclosed" case: it was previously invisible to this check entirely (out
 * of scope, relying on a backend M6 finding that in fact only fires when the
 * CELL has no disclosure whatsoever, see `flowState.ts`'s own comment at the
 * call site for why that reliance was wrong).
 */
export function casArticles(
  conditions: readonly StatementCondition[],
  articles: readonly string[],
  reviewerBindings: ReadonlyMap<string, readonly string[]>,
): Map<string | null, string[]> {
  // `null` is a real key here (a CAS-less "nameless slot" disclosure, e.g.
  // halogen-free's elemental fields), not "no CAS at all", matching the
  // original `cases.add(normalizeCas(c.cas))`, which added `null` the same
  // way. Only `reviewerBindings` can never carry it (`readBindings` requires
  // a non-null `substanceCas` on the row it reads).
  const serverBound = new Map<string | null, string[]>()
  // Beside each list, so a 10k-article binding dedups in one pass.
  const serverBoundSeen = new Map<string | null, Set<string>>()
  const agnostic = new Set<string | null>()
  const seen = new Set<string | null>()
  for (const c of conditions) {
    const cas = normalizeCas(c.cas)
    seen.add(cas)
    const bound = c.articleNumbers ?? []
    if (bound.length === 0) {
      agnostic.add(cas)
      continue
    }
    const list = serverBound.get(cas) ?? []
    const listed = serverBoundSeen.get(cas) ?? new Set<string>()
    for (const a of bound) {
      if (listed.has(a)) continue
      listed.add(a)
      list.push(a)
    }
    serverBound.set(cas, list)
    serverBoundSeen.set(cas, listed)
  }
  for (const cas of reviewerBindings.keys()) seen.add(cas)
  const out = new Map<string | null, string[]>()
  for (const cas of seen) {
    const reviewerBound = cas != null ? reviewerBindings.get(cas) : undefined
    if (reviewerBound && reviewerBound.length > 0) {
      out.set(cas, [...reviewerBound])
    } else if (!agnostic.has(cas) && serverBound.has(cas)) {
      out.set(cas, serverBound.get(cas)!)
    } else {
      out.set(cas, [...articles])
    }
  }
  return out
}
